import { atom } from 'jotai';
import { FieldPath } from 'react-hook-form';
import { filterRestrictedColumns } from '../../../components/ColumnManagement/utils';
import { KpiPeriod } from '../../../data-models/company-financials.data-model';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import {
  createKpiDisplayPreferences,
  createPanelPreferences,
  FinancialsDateViewPreferences,
  getOrgPreferenceKey,
  KpiDisplayPreferences,
  PanelPreferences,
  PreferenceKey,
  PreferenceScope,
} from '../../../data-models/preferences-v3.data-model';
import { COMPANY_VIEW_TYPE } from '../../../data-models/view-config.data-model';
import { CompanyFundMetricsDataModel } from '../../../schemas/CompanyFundMetrics.schema';
import { createCompanyOrDefaultPrefAtoms, preferenceAtom } from '../../../util/preferences-service-v3';
import { ICompanyViewModel } from '../../../view-models/company.view-model';
import { MAX_RANGE } from '../../CompanyProfiles/components/FinancialsDatePickers/financeDatePickerUtils';
import { columnNameToPathState } from '../../PortfolioOverview/state/ViewStateJ';

const companyProfileSummaryPrefAtom = preferenceAtom<PanelPreferences>({
  preferenceKey: `${PreferenceKey.companySummaryDetails}.$default`,
  scope: PreferenceScope.ORGANIZATION,
});

export const selectedDetailsFieldsOrgState = atom(
  async (get) => {
    const selectedFields = (get(companyProfileSummaryPrefAtom)?.selectedFields ??
      []) as FieldPath<ICompanyDataModel>[];
    const columnsToHide = get(selectedColumnsGridDisplayFieldsOrgState);
    const columnNameToPathMap = await get(columnNameToPathState(COMPANY_VIEW_TYPE.RETURN_FORECAST));

    return filterRestrictedColumns(
      selectedFields,
      columnsToHide,
      columnNameToPathMap
    ) as FieldPath<ICompanyDataModel>[];
  },
  (get, set, selectedFields: string[]) => {
    set(companyProfileSummaryPrefAtom, {
      ...get(companyProfileSummaryPrefAtom),
      selectedFields,
    });
  }
);

const companyProfileInvestmentsPrefAtom = preferenceAtom<PanelPreferences>({
  preferenceKey: `${PreferenceKey.companySummaryInvestments}.$default`,
  scope: PreferenceScope.ORGANIZATION,
});

export const selectedMetricsFieldsOrgState = atom(
  async (get) => {
    const selectedFields = (get(companyProfileInvestmentsPrefAtom)?.selectedFields ??
      []) as FieldPath<CompanyFundMetricsDataModel>[];
    const columnsToHide = get(selectedColumnsGridDisplayFieldsOrgState);
    const columnNameToPathMap = await get(columnNameToPathState(COMPANY_VIEW_TYPE.RETURN_FORECAST));

    return filterRestrictedColumns(
      selectedFields,
      columnsToHide,
      columnNameToPathMap
    ) as FieldPath<CompanyFundMetricsDataModel>[];
  },
  (get, set, selectedFields: string[]) => {
    set(companyProfileInvestmentsPrefAtom, {
      ...get(companyProfileInvestmentsPrefAtom),
      selectedFields,
    });
  }
);

const companyProfileListOverviewPrefAtom = preferenceAtom<PanelPreferences>({
  preferenceKey: `${PreferenceKey.companyListsOverview}.$default`,
  scope: PreferenceScope.ORGANIZATION,
});

export const selectedListOverviewFieldsOrgState = atom(
  (get) => {
    return (get(companyProfileListOverviewPrefAtom)?.selectedFields ?? []) as FieldPath<ICompanyViewModel>[];
  },
  (get, set, selectedFields: string[]) => {
    set(companyProfileListOverviewPrefAtom, {
      ...get(companyProfileListOverviewPrefAtom),
      selectedFields,
    });
  }
);

const companyProfileKPIChartPrefAtom = preferenceAtom<TSelectedKPIChartFields>({
  preferenceKey: `${PreferenceKey.companySummaryKPICharts}.$default`,
  scope: PreferenceScope.ORGANIZATION,
});

export type TSelectedKPIChartFields = {
  selectedKPIOne: number | null;
  selectedKPITwo: number | null;
  kpi1ShowBudget: boolean;
  singleYAxis?: boolean;
};

export const selectedKPIChartFieldsOrgState = atom(
  (get) => {
    return (
      get(companyProfileKPIChartPrefAtom) ?? {
        selectedKPIOne: null,
        selectedKPITwo: null,
        kpi1ShowBudget: false,
      }
    );
  },
  (get, set, config: TSelectedKPIChartFields) => {
    set(companyProfileKPIChartPrefAtom, {
      ...get(companyProfileKPIChartPrefAtom),
      ...config,
    });
  }
);

const companyProfileFinancialSettingsPrefAtom = preferenceAtom<PanelPreferences>({
  preferenceKey: `${PreferenceKey.companySummaryFinancialSettings}.$default`,
  scope: PreferenceScope.ORGANIZATION,
});

export const selectedFinancialSettingsFieldsOrgState = atom(
  (get) => {
    return get(companyProfileFinancialSettingsPrefAtom)?.selectedFields ?? ['reportedOnly', 'showBudget'];
  },
  (get, set, selectedFields: string[]) => {
    set(companyProfileFinancialSettingsPrefAtom, {
      ...get(companyProfileFinancialSettingsPrefAtom),
      selectedFields,
    });
  }
);

const leadershipPanelPrefAtom = preferenceAtom<PanelPreferences>({
  preferenceKey: `${PreferenceKey.companySummaryLeadership}.$default`,
  scope: PreferenceScope.ORGANIZATION,
});

export const showLeadershipCardOrgState = atom(
  (get) => get(leadershipPanelPrefAtom)?.visible ?? true,
  (get, set, visible: boolean) => {
    set(leadershipPanelPrefAtom, createPanelPreferences({ ...get(leadershipPanelPrefAtom), visible }));
  }
);

export const DEFAULT_FINANCIALS_DATE_VIEW: FinancialsDateViewPreferences = {
  frequency: KpiPeriod.quarter,
  pastRange: MAX_RANGE,
  futureRange: MAX_RANGE,
};

const financialsDateViewOrgPrefAtom = preferenceAtom<FinancialsDateViewPreferences>({
  preferenceKey: getOrgPreferenceKey(PreferenceKey.defaultFinancialsDateView, null),
  scope: PreferenceScope.ORGANIZATION,
});

export const financialsDefaultDateViewOrgState = atom(
  (get) => {
    return get(financialsDateViewOrgPrefAtom) ?? DEFAULT_FINANCIALS_DATE_VIEW;
  },
  (get, set, config: FinancialsDateViewPreferences) => {
    set(financialsDateViewOrgPrefAtom, config);
  }
);

const columnsGridDisplayPrefAtom = preferenceAtom<PanelPreferences>({
  preferenceKey: `${PreferenceKey.columnGridDisplay}.$default`,
  scope: PreferenceScope.ORGANIZATION,
});

export const selectedColumnsGridDisplayFieldsOrgState = atom(
  (get) => {
    return get(columnsGridDisplayPrefAtom)?.selectedFields ?? [];
  },
  (get, set, selectedFields: string[]) => {
    set(columnsGridDisplayPrefAtom, {
      ...get(columnsGridDisplayPrefAtom),
      selectedFields,
    });
  }
);

const portfolioInitialViewPrefAtom = preferenceAtom<number | null>({
  preferenceKey: getOrgPreferenceKey(PreferenceKey.portfolioLandingView, null),
  scope: PreferenceScope.ORGANIZATION,
});

export const portfolioDefaultViewIdState = atom(
  (get) => get(portfolioInitialViewPrefAtom) ?? null,
  (get, set, viewId: number | null) => {
    set(portfolioInitialViewPrefAtom, viewId);
  }
);

export const selectedKpiConfigCompanyIdAtom = atom<number | null>(null);

const kpiDisplayPref = createCompanyOrDefaultPrefAtoms<KpiDisplayPreferences>({
  preferenceKey: PreferenceKey.financialsKpiDisplay,
  defaultValue: createKpiDisplayPreferences(),
  selectedCompanyIdAtom: selectedKpiConfigCompanyIdAtom,
});

export const financialsKpiDisplayPrefAtom = kpiDisplayPref.orgDefaultAtom;
export const kpiDisplayForCompanyPrefAtom = kpiDisplayPref.companyOverrideAtom;
export const activeKpiDisplayPrefAtom = kpiDisplayPref.activeAtom;
